import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {PageItem} from '@models/sidebar/page-item';
import {catchError} from 'rxjs/operators';
import {BucketBrowserPageService} from '@services/left-menu/page/bucket-browser-page/bucket-browser-page.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {MenuItem} from '@models/sidebar/menu-item';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {ExternalUrlService} from '@services/left-menu/page/external-url-page/external-url.service';
import {FormPageService} from '@services/left-menu/page/form-page/form-page.service';
import {UserDefinedPageService} from '@services/left-menu/page/user-defined-page/user-defined-page.service';
import {TableauPageService} from '@services/left-menu/page/tableau-page/tableau-page.service';
import {FramePageService} from '@services/left-menu/page/frame-page/frame-page.service';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {
  StatusEventGroupPageService
} from '@services/pages/status-event-group-page/configuration/status-event-group-page.service';
import {TemplatePagesService} from '@services/left-menu/page/template-user-defined-page/template-pages.service';

@Injectable({
  providedIn: 'root'
})
export class DeleteObservableService {

  constructor(private bucketBrowserPageService: BucketBrowserPageService,
              private leftMenuService: LeftMenuService,
              private statusEventPageService: StatusEventPageService,
              private statusEventGroupPageService: StatusEventGroupPageService,
              private externalUrlService: ExternalUrlService,
              private formPageService: FormPageService,
              private userDefinedPageService: UserDefinedPageService,
              private templatePagesService: TemplatePagesService,
              private tableauPageService: TableauPageService,
              private framePageService: FramePageService,
              private favoriteNotificationService: FavoriteNotificationService) {
  }

  public setBucketBrowserPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem): void {
    if (menuItem.machineId !== null && pageItem.id !== null && pageItem.pageId !== null && pageItem.pageId) {
      observables.push(this.bucketBrowserPageService.deleteBucketBrowserPage(menuItem.machineId, pageItem.pageId, pageItem.id).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setStatusEventPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem): void {
    if (menuItem.machineId !== null && pageItem.id !== null && pageItem.pageId !== null && pageItem.pageId) {
      observables.push(this.statusEventPageService.deleteEventStatusPage(menuItem.machineId, pageItem.pageId, pageItem.id).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setExternalUrlDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, machineOrGroupId: string, isParentMachineGroup: boolean, menuItem: MenuItem): void {
    if ((menuItem.machineId !== null || menuItem.id !== null) && pageItem.id !== null && pageItem.pageId !== null && pageItem.pageId) {
      observables.push(this.externalUrlService.deleteExternalUrl(machineOrGroupId, pageItem.pageId, pageItem.id, isParentMachineGroup).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setFormPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem): void {
    if (menuItem.machineId !== null && pageItem.id !== null && pageItem.pageId !== null && pageItem.pageId) {
      observables.push(this.formPageService.deleteFormPage(menuItem.machineId, pageItem.pageId, pageItem.id).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setNodeDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem, index: number): void {
    if (menuItem.machineId && pageItem.id && menuItem.id) {
      observables.push(this.leftMenuService.deleteNode(menuItem.machineId, pageItem.id, menuItem.name, index).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setUserDefinedPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem, isSharedPage: boolean): void {
    let parentId: string | null | undefined = this.getParentId(menuItem);
    if (menuItem.machineId !== null && pageItem.pageId && pageItem.id) {
      let isParentMachine = menuItem.isMachine;
      if (pageItem.parentId !== menuItem.machineId) {
        isParentMachine = false;
        parentId = pageItem.parentId;
      }
      if (parentId) {
        observables.push(this.userDefinedPageService.delete(menuItem.machineId, pageItem.pageId, pageItem.id, parentId, isParentMachine, isSharedPage).pipe(catchError(err => of(err))));
        const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
        menuNotification.pageId = pageItem.id;
        menuNotifications.push(menuNotification);
        this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
      }
    }
  }

  public setTemplatePageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem): void {
    if (menuItem.machineId !== null && pageItem.pageId && pageItem.id) {
      observables.push(this.templatePagesService.delete(menuItem.machineId, pageItem.pageId).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setTableauPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, machineOrGroupId: string, isParentMachineGroup: boolean, menuItem: MenuItem): void {
    if (machineOrGroupId && pageItem.id && menuItem.id && pageItem.pageId) {
      observables.push(this.tableauPageService.deleteTableauPage(machineOrGroupId, pageItem.id, pageItem.pageId, isParentMachineGroup).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setFramePageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, machineOrGroupId: string, isParentMachineGroup: boolean, menuItem: MenuItem): void {
    if (machineOrGroupId && pageItem.id && menuItem.id && pageItem.pageId) {
      observables.push(this.framePageService.deleteFramePage(machineOrGroupId, pageItem.pageId, pageItem.id, isParentMachineGroup).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  public setStatusEventGroupPageDeleteObservable(observables: Observable<any>[], menuNotifications: MenuNotification[], pageItem: PageItem, menuItem: MenuItem): void {
    if (!!Number(menuItem.id) && pageItem.id !== null && pageItem.pageId !== null && pageItem.pageId) {
      observables.push(this.statusEventGroupPageService.delete(Number(menuItem.id), pageItem.pageId, pageItem.id).pipe(catchError(err => of(err))));
      const menuNotification = new MenuNotification(OperationType.DELETE_PAGE, this.getParentId(menuItem), pageItem.name, '');
      menuNotification.pageId = pageItem.id;
      menuNotifications.push(menuNotification);
      this.favoriteNotificationService.addPendingFavoriteNotifications(new MenuNotification(OperationType.DELETE_PAGE, pageItem.id, pageItem.name, ''));
    }
  }

  private getParentId(menuItem: MenuItem): string | null {
    return this.leftMenuService.getParentId(menuItem);
  }
}
